import { StoneColor } from '../types';
/**
 * 围棋棋盘类
 * 负责棋盘状态的管理和基本操作
 */
export class Board {
    constructor(size = 19) {
        this.state = this.createEmptyBoard(size);
    }
    /**
     * 创建空棋盘
     */
    createEmptyBoard(size) {
        const stones = [];
        for (let i = 0; i < size; i++) {
            stones[i] = new Array(size).fill(StoneColor.EMPTY);
        }
        return {
            size,
            stones,
            moveNumber: 0,
            captures: {
                black: 0,
                white: 0
            },
            history: []
        };
    }
    /**
     * 获取棋盘状态
     */
    getState() {
        return { ...this.state };
    }
    /**
     * 获取棋盘大小
     */
    getSize() {
        return this.state.size;
    }
    /**
     * 获取指定位置的棋子
     */
    getStone(coord) {
        if (!this.isValidCoordinate(coord)) {
            throw new Error(`无效坐标: (${coord.x}, ${coord.y})`);
        }
        return this.state.stones[coord.y - 1][coord.x - 1];
    }
    /**
     * 放置棋子
     */
    placeStone(coord, color) {
        if (!this.isValidCoordinate(coord)) {
            throw new Error(`无效坐标: (${coord.x}, ${coord.y})`);
        }
        if (this.getStone(coord) !== StoneColor.EMPTY) {
            throw new Error(`位置已有棋子: (${coord.x}, ${coord.y})`);
        }
        this.state.stones[coord.y - 1][coord.x - 1] = color;
    }
    /**
     * 移除棋子
     */
    removeStone(coord) {
        if (!this.isValidCoordinate(coord)) {
            throw new Error(`无效坐标: (${coord.x}, ${coord.y})`);
        }
        this.state.stones[coord.y - 1][coord.x - 1] = StoneColor.EMPTY;
    }
    /**
     * 检查坐标是否有效
     */
    isValidCoordinate(coord) {
        return (coord.x >= 1 &&
            coord.x <= this.state.size &&
            coord.y >= 1 &&
            coord.y <= this.state.size);
    }
    /**
     * 检查坐标是否在棋盘内
     */
    isInBoard(x, y) {
        return x >= 1 && x <= this.state.size && y >= 1 && y <= this.state.size;
    }
    /**
     * 获取相邻坐标
     */
    getNeighbors(coord) {
        const neighbors = [];
        const directions = [
            { dx: 0, dy: -1 }, // 上
            { dx: 1, dy: 0 }, // 右
            { dx: 0, dy: 1 }, // 下
            { dx: -1, dy: 0 } // 左
        ];
        for (const dir of directions) {
            const nx = coord.x + dir.dx;
            const ny = coord.y + dir.dy;
            if (this.isInBoard(nx, ny)) {
                neighbors.push({ x: nx, y: ny });
            }
        }
        return neighbors;
    }
    /**
     * 获取对角线坐标
     */
    getDiagonals(coord) {
        const diagonals = [];
        const directions = [
            { dx: -1, dy: -1 }, // 左上
            { dx: 1, dy: -1 }, // 右上
            { dx: -1, dy: 1 }, // 左下
            { dx: 1, dy: 1 } // 右下
        ];
        for (const dir of directions) {
            const nx = coord.x + dir.dx;
            const ny = coord.y + dir.dy;
            if (this.isInBoard(nx, ny)) {
                diagonals.push({ x: nx, y: ny });
            }
        }
        return diagonals;
    }
    /**
     * 查找相连的棋子
     */
    findConnectedGroup(coord, color) {
        const group = [];
        const visited = new Set();
        const dfs = (current) => {
            const key = `${current.x},${current.y}`;
            if (visited.has(key))
                return;
            visited.add(key);
            if (this.getStone(current) === color) {
                group.push(current);
                const neighbors = this.getNeighbors(current);
                for (const neighbor of neighbors) {
                    dfs(neighbor);
                }
            }
        };
        dfs(coord);
        return group;
    }
    /**
     * 计算棋块的气
     */
    calculateLiberties(group) {
        const liberties = new Set();
        for (const coord of group) {
            const neighbors = this.getNeighbors(coord);
            for (const neighbor of neighbors) {
                if (this.getStone(neighbor) === StoneColor.EMPTY) {
                    liberties.add(`${neighbor.x},${neighbor.y}`);
                }
            }
        }
        return Array.from(liberties).map(key => {
            const [x, y] = key.split(',').map(Number);
            return { x, y };
        });
    }
    /**
     * 判断棋块是否有气
     */
    hasLiberties(group) {
        return this.calculateLiberties(group).length > 0;
    }
    /**
     * 清空棋盘
     */
    clear() {
        this.state = this.createEmptyBoard(this.state.size);
    }
    /**
     * 克隆棋盘
     */
    clone() {
        const newBoard = new Board(this.state.size);
        newBoard.state = JSON.parse(JSON.stringify(this.state));
        return newBoard;
    }
    /**
     * 转换为字符串表示（用于调试）
     */
    toString() {
        let result = '';
        const size = this.state.size;
        // 列坐标
        result += '   ';
        for (let x = 1; x <= size; x++) {
            result += String.fromCharCode('A'.charCodeAt(0) + (x > 8 ? x : x - 1)) + ' ';
        }
        result += '\n';
        // 棋盘内容
        for (let y = 1; y <= size; y++) {
            // 行坐标
            result += y.toString().padStart(2) + ' ';
            for (let x = 1; x <= size; x++) {
                const stone = this.getStone({ x, y });
                switch (stone) {
                    case StoneColor.EMPTY:
                        result += '· ';
                        break;
                    case StoneColor.BLACK:
                        result += '● ';
                        break;
                    case StoneColor.WHITE:
                        result += '○ ';
                        break;
                }
            }
            result += '\n';
        }
        return result;
    }
    /**
     * 从SGF字符串加载棋盘
     */
    loadFromSGF(sgf) {
        // TODO: 实现SGF解析
        throw new Error('SGF解析功能待实现');
    }
    /**
     * 导出为SGF字符串
     */
    toSGF() {
        // TODO: 实现SGF生成
        throw new Error('SGF生成功能待实现');
    }
    /**
     * 获取棋盘哈希（用于重复局面检测）
     */
    getHash() {
        let hash = '';
        for (let y = 1; y <= this.state.size; y++) {
            for (let x = 1; x <= this.state.size; x++) {
                hash += this.getStone({ x, y }).toString();
            }
        }
        return hash;
    }
    /**
     * 统计棋子数量
     */
    countStones(color) {
        let count = 0;
        for (let y = 1; y <= this.state.size; y++) {
            for (let x = 1; x <= this.state.size; x++) {
                if (this.getStone({ x, y }) === color) {
                    count++;
                }
            }
        }
        return count;
    }
    /**
     * 获取所有空点
     */
    getEmptyPoints() {
        const emptyPoints = [];
        for (let y = 1; y <= this.state.size; y++) {
            for (let x = 1; x <= this.state.size; x++) {
                if (this.getStone({ x, y }) === StoneColor.EMPTY) {
                    emptyPoints.push({ x, y });
                }
            }
        }
        return emptyPoints;
    }
}
//# sourceMappingURL=Board.js.map